<template>
  <v-container fluid class="pa-md-12">
    <v-row wrap>
      <v-col cols="12">
        <v-card class="pa-5 px-md-12 py-md-6" min-height="700px">
          <v-row v-if="funFullPageLoader">
            <v-col cols="12" class="text-center">
              <Vue3Lottie
                :animationData="FunLoaderJSON"
                :width="200"
                :height="200"
                v-motion
                :initial="{ opacity: 0, y: 30 }"
                :enter="{ opacity: 1, y: 0, scale: 1 }"
                :delay="300"
              />
              <div
                v-motion
                :initial="{ opacity: 0, y: 30 }"
                :enter="{ opacity: 1, y: 0, scale: 1 }"
                :delay="600"
                class="text-caption"
              >
                Generating...
              </div>
            </v-col>
          </v-row>
          <v-row wrap v-else>
            <v-col cols="12">
              <div
                v-motion
                :initial="{ opacity: 0, x: 10 }"
                :enter="{ opacity: 1, x: 0, scale: 1 }"
                :delay="0"
                class="font-weight-bold"
              >
                Create Session
              </div>
            </v-col>
            <v-col cols="12">
              <v-divider></v-divider>
            </v-col>
            <v-col cols="12">
              <div class="text-disabled text-caption">Mode</div>
              <v-radio-group v-model="sessionMode" color="progress" class="mt-2">
                <v-radio label="Tutor Mode" value="tutor"></v-radio>
                <v-radio label="Mock Mode" value="mock"></v-radio>
              </v-radio-group>

              <v-alert
                class="mb-4"
                icon="mdi-school-outline"
                :title="enableTestMode ? 'Mock Mode' : 'Tutor Mode'"
                :text="
                  enableTestMode
                    ? 'Exam-style session: results are shown at the end only. A timer is enabled automatically to mimic the real exam.'
                    : 'Customise your session by choosing topics and the number of questions. Answers are shown as you go along.'
                "
                :type="enableTestMode ? 'success' : 'info'"
                variant="tonal"
              ></v-alert>

              <v-checkbox
                v-model="enableTimer"
                :disabled="enableTestMode"
                color="progress"
                label="Enable Timer"
                hint="If enabled, it will show a countdown timer. In Mock Mode, the session ends when the timer runs out."
                persistent-hint
              ></v-checkbox>
            </v-col>
            <v-col v-if="enableTimer" cols="12" md="4">
              <v-text-field
                v-model="timerAmount"
                color="progress"
                base-color="progress"
                label="Timer (minutes)"
                type="number"
                hint="Default ratio: 90 questions in 2 hours. Approx. 3 minutes per 4 questions, like the real exam."
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-divider></v-divider>
            </v-col>
            <v-col cols="12" class="ma-0 pa-0 pl-3">
              <div
                class="text-disabled text-caption"
                v-motion
                :initial="{ opacity: 0, x: 10 }"
                :enter="{ opacity: 1, x: 0, scale: 1 }"
                :delay="0"
              >
                Choose which topics you would like to be tested on.
              </div>
            </v-col>
            <v-col cols="12">
              <v-checkbox
                v-if="parentCats.length > 1"
                :disabled="enableTestMode"
                label="Select All"
                :model-value="masterChecked"
                @update:modelValue="handleMasterCheckbox"
                :indeterminate="showMasterIndeterminate"
                color="progress"
              ></v-checkbox>

              <v-expansion-panels
                :multiple="parentCats.length === 1"
                :model-value="parentCats.length === 1 ? [0] : null"
              >
                <v-expansion-panel
                  v-for="(pcat, i) in parentCats"
                  :key="pcat.parent"
                  v-motion
                  :initial="{ opacity: 0, y: 10 }"
                  :enter="{ opacity: 1, y: 0, scale: 1 }"
                  :delay="100 * (i + 1)"
                >
                  <v-expansion-panel-title
                    class="min-height: 150px; !important"
                    expand-icon="mdi-chevron-down-circle"
                    collapse-icon="mdi-chevron-up-circle"
                  >
                    <template v-slot:actions="{ expanded }">
                      <v-icon
                        v-if="expanded"
                        :size="mobile ? 'small' : 'x-large'"
                        color="progress"
                        class="mb-5"
                        >mdi-chevron-up-circle</v-icon
                      >
                      <v-icon
                        v-else
                        :size="mobile ? 'small' : 'x-large'"
                        color="progress"
                        class="mb-5"
                        >mdi-chevron-down-circle</v-icon
                      >
                    </template>
                    <v-row no-gutters>
                      <v-col md="1" cols="2" class="align-self-center">
                        <div @click.stop style="display: inline-block">
                          <v-checkbox
                            :disabled="enableTestMode"
                            @update:modelValue="
                              (val) => handleParentCheckbox(val, pcat)
                            "
                            :model-value="
                              checkAllChildCatSelected(pcat.children)
                            "
                            :indeterminate="
                              checkSomeChildCatSelected(pcat.children)
                            "
                            color="progress"
                          ></v-checkbox>
                        </div>
                      </v-col>
                      <v-col
                        md="4"
                        cols="7"
                        class="align-self-center"
                        :style="`${mobile ? 'font-size: smaller' : ''}`"
                      >
                        <div class="pb-5">
                          {{ pcat.parent }}
                        </div>
                      </v-col>
                      <v-col
                        md="3"
                        cols="3"
                        class="align-self-center"
                        :style="`${mobile ? 'font-size: smaller' : ''}`"
                      >
                        <div class="pb-5">
                          {{ calcNumOfChildQuestions(pcat.children) }} Questions
                        </div>
                      </v-col>
                      <v-col
                        md="4"
                        class="align-self-center hidden-xs pr-6"
                        :style="`${mobile ? 'font-size: smaller' : ''}`"
                      >
                        <v-progress-linear
                          class="mb-1"
                          :model-value="
                            getChildrenCompletionData(pcat.children).perc
                          "
                          color="progress"
                          rounded
                          height="10"
                        ></v-progress-linear>
                        <span class="text-caption text-disabled">
                          Completed:
                          {{ getChildrenCompletionData(pcat.children).perc }}%
                          ({{
                            getChildrenCompletionData(pcat.children).completed
                          }}
                          of
                          {{ getChildrenCompletionData(pcat.children).total }})
                        </span>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-title>

                  <v-expansion-panel-text>
                    <v-table>
                      <thead>
                        <tr>
                          <th
                            style="width: 50px"
                            class="text-left font-weight-bold pt-5"
                          >
                            <!-- create space to align properly -->
                          </th>
                          <th class="text-left font-weight-bold">Topics</th>
                          <th class="text-left font-weight-bold">Questions</th>
                          <th class="text-left font-weight-bold hidden-xs">
                            Stats
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(catId, i) in pcat.children" :key="catId">
                          <td class="pt-5">
                            <v-checkbox
                              :disabled="enableTestMode"
                              v-model="categoriesSelected"
                              :value="catId"
                              color="progress"
                            ></v-checkbox>
                          </td>
                          <td :style="`${mobile ? 'font-size: smaller' : ''}`">
                            {{ categoriesDict[catId]?.name }}
                          </td>
                          <td :style="`${mobile ? 'font-size: smaller' : ''}`">
                            {{ categoriesDict[catId]?.stats?.numberOfQuestions }}
                            Question{{
                              categoriesDict[catId]?.stats
                                ?.numberOfQuestions !== 1
                                ? "s"
                                : ""
                            }}
                          </td>

                          <td class="hidden-xs">
                            <v-progress-linear
                              :model-value="
                                perc(
                                  getUserCatStat(catId)
                                    .uniqueQuestionsAttempted,
                                  categoriesDict[catId]?.stats
                                    ?.numberOfQuestions,
                                )
                              "
                              color="progress"
                              rounded
                              height="10"
                            />
                            <div class="cat-stats text-caption text-disabled">
                              <div class="label">Completed</div>
                              <div class="value">
                                {{
                                  perc(
                                    getUserCatStat(catId)
                                      .uniqueQuestionsAttempted,
                                    categoriesDict[catId]?.stats
                                      ?.numberOfQuestions,
                                  )
                                }}% ({{
                                  getUserCatStat(catId).uniqueQuestionsAttempted
                                }}
                                of
                                {{ categoriesDict[catId]?.stats?.numberOfQuestions }})
                              </div>

                              <div class="label">Flagged</div>
                              <div class="value">
                                <span v-if="simpleSmallLoader">
                                  <v-progress-circular
                                    indeterminate
                                    color="progress"
                                    size="15"
                                    width="2"
                                  />
                                </span>
                                <span v-else>
                                  {{ flaggedCountMap[catId] ?? 0 }}
                                </span>
                              </div>

                              <div class="label">Accuracy</div>
                              <div class="value">
                                {{
                                  perc(
                                    getUserCatStat(catId).questionsCorrect,
                                    getUserCatStat(catId).questionsAttempted,
                                  )
                                }}%
                              </div>

                              <div class="label">Peer Accuracy</div>
                              <div class="value">
                                {{
                                  perc(
                                    categoriesDict[catId]?.stats
                                      ?.totalCorrectAnswers,
                                    categoriesDict[catId]?.stats
                                      ?.totalQuestionAttempts,
                                  )
                                }}%
                              </div>

                              <div class="label">High-yield</div>
                              <div class="value">
                                {{
                                  statsStore.categoryYieldCounts[
                                    catId
                                  ]?.high ?? 0
                                }}
                                of
                                {{
                                  statsStore.categoryYieldCounts[
                                    catId
                                  ]?.total ?? 0
                                }}
                                ({{
                                  perc(
                                    statsStore.categoryYieldCounts[
                                      catId
                                    ]?.high ?? 0,
                                    statsStore.categoryYieldCounts[
                                      catId
                                    ]?.total ?? 0,
                                  )
                                }}%)
                              </div>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </v-table>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
            <v-col cols="12">
              <v-divider></v-divider>
            </v-col>
            <v-col cols="12">
              <div class="text-disabled text-caption">More options</div>
            </v-col>
            <v-col cols="12">
              <v-radio-group
                v-if="!enableTestMode"
                v-model="questionType"
                color="progress"
                label="Question Type"
              >
                <v-radio
                  label="Unattempted questions only"
                  value="unattempted"
                ></v-radio>
                <v-radio
                  label="Attempted questions only"
                  value="attempted"
                ></v-radio>
                <v-radio label="All questions" value="all"></v-radio>
                <v-radio
                  :disabled="wrongOnlyCount === 0"
                  value="incorrect"
                >
                  <template #label>
                    <span>
                      <v-tooltip text="Last attempt">
                        <template #activator="{ props }">
                          <span
                            v-bind="props"
                            style="
                              text-decoration: underline dotted;
                              text-underline-offset: 2px;
                            "
                            >Incorrectly</span
                          >
                        </template>
                      </v-tooltip>
                      answered questions
                      <span class="text-caption text-disabled"
                        >({{ wrongOnlyCount }} found)</span
                      >
                    </span>
                  </template>
                </v-radio>
                <v-radio
                  v-if="!simpleSmallLoader"
                  label="Flagged questions only"
                  value="flagged"
                ></v-radio>
                <v-progress-circular
                  v-else
                  class="ml-2 mt-2"
                  indeterminate
                  color="progress"
                  size="25"
                  width="2"
                ></v-progress-circular>
              </v-radio-group>
              <v-checkbox
                v-if="
                  !simpleSmallLoader && bank?.name === 'part-2-question-bank'
                "
                v-model="includeLowYield"
                :disabled="enableTestMode"
                color="progress"
                class="ml-2 mb-6"
                label="Include low yield questions"
                hint="High yield questions are derived from actual past exam questions."
                persistent-hint
              ></v-checkbox>

              <div>
                <v-alert
                  icon="mdi-school-outline"
                  :title="
                    questionSeenStatus === 'unseen'
                      ? 'Smart Study is Off'
                      : 'Smart Study is On'
                  "
                  :text="
                    enableTestMode
                      ? 'Questions are selected randomly from all topics using the exam distribution. Previous attempts do not affect selection.'
                      : questionSeenStatus === 'unseen'
                      ? `You'll receive a random set of unattempted questions. To enable Smart Study, include some attempted questions.`
                      : 'When Smart Study is on, you will be given questions selected by our Smart Study algorithm. Our algorithm tailors your study experience by analysing your past performance and identifying areas for improvement. It provides a mix of questions to reinforce your knowledge and challenge your learning.'
                  "
                  :type="questionSeenStatus === 'unseen' ? 'info' : 'success'"
                  variant="tonal"
                ></v-alert>
              </div>
            </v-col>
            <v-col cols="12">
              <v-divider></v-divider>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="numberOfQuestions"
                :disabled="enableTestMode"
                color="progress"
                base-color="progress"
                label="Number of Questions"
                type="number"
                :hint="`Max: ${effectivePossibleQuestions} question${
                  effectivePossibleQuestions !== 1 ? 's' : ''
                } (based on options).`"
                :error-messages="questionCountError"
                persistent-hint
              ></v-text-field>
              <div
                v-if="effectivePossibleQuestions == 100"
                class="mt-1 ml-3 text-caption text-disabled"
              >
                <i>*There is a hard limit of 100 questions per session.</i>
              </div>
            </v-col>
            <v-col
              v-if="showAvailabilityNotice"
              cols="12"
            >
              <v-alert
                :type="effectivePossibleQuestions === 0 ? 'warning' : 'info'"
                variant="tonal"
                icon="mdi-book-open-page-variant-outline"
                :title="availabilityTitle"
                :text="availabilityText"
              ></v-alert>
              <v-btn
                v-if="matchingReservedUnseen > 0"
                class="mt-3"
                color="primary"
                variant="outlined"
                prepend-icon="mdi-history"
                @click="router.push('/sessions')"
              >
                View unfinished sessions
              </v-btn>
            </v-col>
            <!--
              If they are a subcriber then always let them start.
              If not then they can generate one test.
             -->
            <v-col
              v-if="
                paymentStore.isSubscribed?.[bank?.name]?.val || isTrialSession
              "
              cols="12"
            >
              <v-btn
                color="primary"
                rounded="lg"
                variant="flat"
                block
                size="large"
                :disabled="
                  categoriesSelected.length == 0 ||
                  numberOfQuestions == 0 ||
                  questionCountError.length > 0 ||
                  simpleSmallLoader
                "
                @click="handleStartClick"
              >
                Start
              </v-btn>
              <div
                v-if="categoriesSelected.length === 0"
                class="text-disabled mt-3"
              >
                You haven't selected any categories using the checkboxes above.
              </div>
            </v-col>
            <v-col v-else cols="12">
              <v-btn
                @click="goToEyeAcademyWebsite"
                block
                color="primary"
                rounded="lg"
                variant="flat"
                size="x-large"
                class="nice-grad-background"
              >
                Subscribe
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import FunLoaderJSON from "@/assets/funLoader.json";
import { ref, computed, onMounted, watch } from "vue";
import { useStatsStore } from "@/store/stats";
import { useDataStore } from "@/store/data";
import { useUserStore } from "@/store/user";
import { useAlertStore } from "@/store/alert";
import { useBankStore } from "@/store/bank";
import { useRoutePerformance } from "@/composables/useRoutePerformance";
import { useDocument, useFirestore } from "vuefire";
import { useDisplay } from "vuetify";
const { mobile } = useDisplay();
const db = useFirestore();
import { collection, doc } from "firebase/firestore";
import router from "@/router";
const statsStore = useStatsStore();
const dataStore = useDataStore();
const userStore = useUserStore();
const alertStore = useAlertStore();
const bankStore = useBankStore();
const routePerformance = useRoutePerformance("sessionSetup");

// Add collection path based on route
const bank = computed(() => {
  if (bankStore.currentBank === "part-1-question-bank") {
    return {
      name: "part-1-question-bank",
      path: "/new_modules/part-1-question-bank/",
    };
  } else if (bankStore.currentBank === "duke-elder-question-bank") {
    return {
      name: "duke-elder-question-bank",
      path: "/new_modules/duke-elder-question-bank/",
    };
  } else {
    return {
      name: "part-2-question-bank",
      path: "",
    };
  }
});

import { usePaymentStore } from "@/store/payment";
const paymentStore = usePaymentStore();

const numberOfQuestions = ref(0);
const creationShortage = ref(null);

watch(numberOfQuestions, (val) => {
  if (val < 0) {
    numberOfQuestions.value = 0;
    timerAmount.value = 0;
  } else {
    timerAmount.value = Math.floor(val * (4 / 3));
  }
});

const parentCats = computed(() => {
  // Early return if categories data isn't loaded yet
  if (!dataStore.categoriesData?.length) return [];

  // Create a map of parent categories and their children
  const parentMap = dataStore.categoriesData.reduce((acc, category) => {
    // Skip categories with 0 questions
    if (category.stats.numberOfQuestions === 0) return acc;

    const parent = category.parent || "Other";
    if (!acc[parent]) {
      acc[parent] = {
        parent,
        children: [],
        totalQuestions: 0,
      };
    }
    acc[parent].children.push(category.id);
    acc[parent].totalQuestions += category.stats.numberOfQuestions;
    return acc;
  }, {});

  // Filter out parent categories with 0 total questions
  return Object.values(parentMap)
    .filter((group) => group.totalQuestions > 0)
    .map((group) => ({
      parent: group.parent,
      children: group.children.sort((a, b) => {
        // Sort children by number of questions (descending)
        const aCount = categoriesDict.value[a]?.stats?.numberOfQuestions || 0;
        const bCount = categoriesDict.value[b]?.stats?.numberOfQuestions || 0;
        return bCount - aCount;
      }),
      totalQuestions: group.totalQuestions,
    }))
    .sort((a, b) => {
      // Sort parents by total questions (descending)
      return b.totalQuestions - a.totalQuestions;
    });
});

const goToEyeAcademyWebsite = () => {
  window.open("https://eye.academy/part2written", "_blank");
};

// When page loads fetch the useQuestions
// so we can show them how many flagged questions they have
// etc. This is also used to calculate the number of possible questions
const userQuestions = ref([]);
const flaggedCount = ref({});
const simpleSmallLoader = ref(true);
const funFullPageLoader = ref(false);

// Derive flagged counts by category from the questions array
const flaggedCountMap = computed(() => {
  const map = {};
  for (const q of userQuestions.value) {
    // q.flagged boolean, q.category is a string id (normalized in backend)
    if (q?.flagged && q?.category) {
      map[q.category] = (map[q.category] || 0) + 1;
    }
  }
  return map;
});

onMounted(async () => {
  routePerformance.markShellReady();
  simpleSmallLoader.value = true;
  try {
    // This also populates statsStore.categoryYieldCounts internally
    userQuestions.value = await statsStore.getUserQuestionsWithMeta();
    routePerformance.markDataReady();
  } catch (error) {
    routePerformance.markRouteError();
    throw error;
  } finally {
    simpleSmallLoader.value = false;
    routePerformance.markInteractionReady();
  }
});

const perc = (num, total) => {
  if (total == 0 || isNaN(num) || isNaN(total)) {
    return 0;
  }
  return ((num / total) * 100).toFixed(1);
};

const percOf = (num1, num2) => {
  if (isNaN(num1) || isNaN(num2)) {
    return 0;
  }
  return ((num1 / 100) * num2).toFixed(1);
};

const getUserCatStat = (catId) => {
  const catStats = statsStore.userStats?.categories?.[catId];
  if (!catStats) {
    return {
      questionsAttempted: 0,
      questionsCorrect: 0,
      uniqueQuestionsAttempted: 0,
    };
  }
  return catStats;
};

// Category selection logic
const categoriesSelected = ref([]);

const selectableCategoryIds = computed(() =>
  (dataStore.categoriesData || [])
    .filter((cat) => (cat?.stats?.numberOfQuestions || 0) > 0)
    .map((cat) => cat.id),
);

watch(
  selectableCategoryIds,
  (ids) => {
    const allowed = new Set(ids);
    const next = (categoriesSelected.value || []).filter((id) =>
      allowed.has(id),
    );
    if (next.length !== categoriesSelected.value.length) {
      categoriesSelected.value = next;
    }
  },
  { immediate: true },
);

const handleMasterCheckbox = (checked) => {
  if (checked) {
    categoriesSelected.value = [...selectableCategoryIds.value];
  } else {
    categoriesSelected.value = [];
  }
};

const masterChecked = computed(() => {
  const total = selectableCategoryIds.value.length;
  return total > 0 && categoriesSelected.value.length === total;
});

const showMasterIndeterminate = computed(() => {
  const total = selectableCategoryIds.value.length;
  const selected = categoriesSelected.value.length;
  return selected > 0 && selected < total;
});
const handleParentCheckbox = (checked, pcat) => {
  const childIds = pcat.children;
  const current = new Set(categoriesSelected.value);
  if (checked) {
    childIds.forEach((id) => current.add(id));
  } else {
    childIds.forEach((id) => current.delete(id));
  }
  categoriesSelected.value = [...current];
};

const checkAllChildCatSelected = (children) => {
  return children.every((cat) => categoriesSelected.value.includes(cat));
};
const checkSomeChildCatSelected = (children) => {
  const selected = children.filter((cat) =>
    categoriesSelected.value.includes(cat),
  );
  return selected.length > 0 && selected.length < children.length;
};

const calcNumOfChildQuestions = (children) => {
  try {
    let total = 0;
    children.forEach((cat) => {
      total += categoriesDict.value[cat]?.stats?.numberOfQuestions || 0;
    });
    return total;
  } catch (e) {
    return 0;
  }
};
// Cache object
const completionDataCache = ref({});
const getChildrenCompletionData = (children) => {
  // Generate a unique key for the children array. This assumes the children array contains unique identifiers.
  const cacheKey = children.join("-");
  // Check if the result is already cached
  if (completionDataCache.value[cacheKey]) {
    return completionDataCache.value[cacheKey];
  }
  try {
    let total = 0;
    let completed = 0;
    children.forEach((cat) => {
      const catStats = getUserCatStat(cat);
      total += categoriesDict.value[cat]?.stats?.numberOfQuestions || 0;
      completed += catStats.uniqueQuestionsAttempted;
    });
    const result = {
      total,
      completed,
      perc: perc(completed, total),
    };
    // Cache the result
    completionDataCache.value[cacheKey] = result;

    return result;
  } catch (e) {
    return 0;
  }
};

// Question type logic
const questionType = ref("unattempted");
const questionSeenStatus = ref("unseen");
const includeLowYield = ref(true);
const wrongOnly = ref(false);

watch(
  questionType,
  (val) => {
    if (val === "unattempted") {
      questionSeenStatus.value = "unseen";
      wrongOnly.value = false;
      return;
    }

    if (val === "attempted") {
      questionSeenStatus.value = "seen";
      wrongOnly.value = false;
      return;
    }

    if (val === "all") {
      questionSeenStatus.value = "both";
      wrongOnly.value = false;
      return;
    }

    if (val === "incorrect") {
      questionSeenStatus.value = "seen";
      wrongOnly.value = true;
      return;
    }

    if (val === "flagged") {
      questionSeenStatus.value = "flagged";
      wrongOnly.value = false;
    }
  },
  { immediate: true },
);

// Test mode logic
const enableTestMode = ref(false);
const enableTimer = ref(false);
const timerAmount = ref(0);

const sessionMode = computed({
  get: () => (enableTestMode.value ? "mock" : "tutor"),
  set: (val) => {
    enableTestMode.value = val === "mock";
  },
});

watch(
  [enableTestMode, selectableCategoryIds],
  ([enabled, categoryIds]) => {
    if (!enabled) return;
    enableTimer.value = true;
    questionType.value = "unattempted";
    includeLowYield.value = true;
    categoriesSelected.value = [...categoryIds];
  },
  { immediate: true },
);
watch(timerAmount, (val) => {
  if (val < 0) {
    timerAmount.value = 0;
  }
});

// Make all the categories data into a dict
const categoriesDict = computed(() => {
  const dict = {};
  dataStore.categoriesData.forEach((cat) => {
    dict[cat.id] = cat;
  });
  return dict;
});

const wrongOnlyCount = computed(() => {
  const selected = categoriesSelected.value || [];
  if (!selected.length) return 0;

  return (userQuestions.value || []).filter((q) => {
    if (!q?.category) return false;
    if (!selected.includes(q.category)) return false;
    if (!(q?.scoredAttempts ?? q?.attempted)) return false;
    const boxNumber = typeof q.boxNumber === "number" ? q.boxNumber : 1;
    if (boxNumber > 1) return false;
    if (!includeLowYield.value && q.yield !== "high") return false;
    return true;
  }).length;
});

const wrongOnlySessionCap = computed(() =>
  clampForTrialAndLimit(wrongOnlyCount.value),
);

const matchingReservedUnseen = computed(() => {
  if (creationShortage.value?.reason === "INSUFFICIENT_QUESTIONS") {
    return creationShortage.value.heldUnseen || 0;
  }
  const counts = statsStore.categoryYieldCounts || {};
  return (categoriesSelected.value || []).reduce((total, categoryId) => {
    const category = counts[categoryId] || {};
    return (
      total +
      (includeLowYield.value
        ? category.reservedUnseen || 0
        : category.reservedUnseenHigh || 0)
    );
  }, 0);
});

const availableUnseenQuestions = computed(() => {
  const counts = statsStore.categoryYieldCounts || {};
  return (categoriesSelected.value || []).reduce((total, categoryId) => {
    const category = counts[categoryId] || {};
    return (
      total +
      (includeLowYield.value
        ? category.availableUnseen || 0
        : category.availableUnseenHigh || 0)
    );
  }, 0);
});

const possibleQuestions = computed(() => {
  const selected = categoriesSelected.value || [];
  if (!selected.length) return 0;

  if (enableTestMode.value) {
    const total = selected.reduce(
      (sum, categoryId) =>
        sum + (categoriesDict.value[categoryId]?.stats?.numberOfQuestions || 0),
      0,
    );
    return clampForTrialAndLimit(total);
  }

  const uq = userQuestions.value || [];
  const includeLow = includeLowYield.value;

  if (wrongOnly.value) {
    return clampForTrialAndLimit(wrongOnlyCount.value);
  }

  // Seen branch: attempted in selected cats with yield filter
  if (questionSeenStatus.value === "seen") {
    const seenTotal = uq.filter(
      (q) =>
        q.attempted > 0 &&
        selected.includes(q.category) &&
        (includeLow || q.yield === "high"),
    ).length;
    return clampForTrialAndLimit(seenTotal);
  }

  // Flagged branch: flagged in selected cats with yield filter
  if (questionSeenStatus.value === "flagged") {
    const flaggedTotal = uq.filter(
      (q) =>
        q.flagged &&
        selected.includes(q.category) &&
        (includeLow || q.yield === "high"),
    ).length;
    return clampForTrialAndLimit(flaggedTotal);
  }

  const unseenTotal = availableUnseenQuestions.value;

  if (questionSeenStatus.value === "unseen") {
    return clampForTrialAndLimit(unseenTotal);
  }

  // All includes questions already in unfinished sessions.
  const seenTotal = uq.filter(
    (q) =>
      q.attempted > 0 &&
      selected.includes(q.category) &&
      (includeLow || q.yield === "high"),
  ).length;

  return clampForTrialAndLimit(
    seenTotal + unseenTotal + matchingReservedUnseen.value,
  );
});

const effectivePossibleQuestions = computed(() => {
  if (creationShortage.value?.reason === "INSUFFICIENT_QUESTIONS") {
    return clampForTrialAndLimit(creationShortage.value.available || 0);
  }
  return possibleQuestions.value;
});

const questionCountError = computed(() => {
  const requested = Number(numberOfQuestions.value);
  if (!Number.isFinite(requested) || requested < 1) return [];
  if (requested > effectivePossibleQuestions.value) {
    return [`Choose ${effectivePossibleQuestions.value} or fewer questions.`];
  }
  return [];
});

const showAvailabilityNotice = computed(
  () =>
    !enableTestMode.value &&
    !simpleSmallLoader.value &&
    categoriesSelected.value.length > 0 &&
    ["unseen", "both"].includes(questionSeenStatus.value),
);

const availabilityTitle = computed(() => {
  const count = effectivePossibleQuestions.value;
  return `${count} question${count === 1 ? "" : "s"} available`;
});

const availabilityText = computed(() => {
  const available = effectivePossibleQuestions.value;
  const reserved = matchingReservedUnseen.value;
  if (questionSeenStatus.value === "unseen") {
    if (available === 0 && reserved > 0) {
      return "All unattempted questions matching your choices are in unfinished sessions. Continue one from My sessions, or change your choices.";
    }
    if (available === 0) {
      return "You have already attempted all questions matching your choices. Include attempted questions or choose other categories.";
    }
    if (reserved > 0) {
      return `Another ${reserved} unseen question${
        reserved === 1 ? " is" : "s are"
      } in your unfinished sessions. You can continue ${
        reserved === 1 ? "it" : "them"
      } from My sessions.`;
    }
    return "These unattempted questions are ready to use in a new session.";
  }

  if (available === 0) {
    return "No questions are available with these choices. Try other categories or filters.";
  }
  if (reserved > 0) {
    return "Includes questions in unfinished sessions. You may see the same question in more than one session.";
  }
  return "Includes attempted and unattempted questions.";
});

// Preserve your clamping rules
function clampForTrialAndLimit(n) {
  let total = Math.min(100, n);
  if (!paymentStore.isSubscribed?.[bank?.value?.name]?.val) {
    if (total > 10) total = 10;
  }
  return total;
}

watch(possibleQuestions, (val) => {
  if (enableTestMode.value) {
    numberOfQuestions.value = Math.min(90, val);
  } else if (Number(numberOfQuestions.value) === 0) {
    numberOfQuestions.value = val;
  }
});

watch(
  [categoriesSelected, questionSeenStatus, includeLowYield, wrongOnly],
  () => {
    creationShortage.value = null;
  },
  { deep: true },
);

// session creation
const handleStartClick = async () => {
  if (questionCountError.value.length > 0) return;
  creationShortage.value = null;
  // create a session
  const sessionDetails = {
    categories: categoriesSelected.value,
    numberOfQuestions: numberOfQuestions.value,
    questionSeenStatus: questionSeenStatus.value,
    flaggedOnly: questionSeenStatus.value === "flagged" ? true : false,
    wrongOnly: wrongOnly.value,
    enableTestMode: enableTestMode.value,
    enableTimer: enableTimer.value,
    timerAmount: timerAmount.value,
    isTrial: isTrialSession.value,
    // new
    includeLowYield: includeLowYield.value,
  };

  // if flagged only then set the questionSeenStatus to seen because
  // flagged only must be seen
  if (sessionDetails.flaggedOnly) {
    sessionDetails.questionSeenStatus = "seen";
  }

  if (sessionDetails.wrongOnly) {
    sessionDetails.questionSeenStatus = "seen";
  }

  // handle loading
  funFullPageLoader.value = true;

  // Add the bank path
  // either path for part 1 or 2
  sessionDetails.collectionPath = bank.value.path;

  // call firebase function
  const result = await dataStore.createUserSession(sessionDetails);
  if (result.success) {
    setTimeout(() => {
      // Route to the correct session path based on the bank
      if (
        bank.value.name === "part-1-question-bank" ||
        bank.value.name === "duke-elder-question-bank"
      ) {
        router.push(`/sessions/${bank.value.name}/${result.sessionId}`);
      } else {
        router.push(`/sessions/${result.sessionId}`);
      }
    }, 2000);
  } else {
    if (result.details?.reason === "INSUFFICIENT_QUESTIONS") {
      creationShortage.value = result.details;
      funFullPageLoader.value = false;
      return;
    }
    setTimeout(() => {
      funFullPageLoader.value = false;
      alertStore.showAlert(
        "There was an error generating the session. Please try again or contact support.",
        "error",
      );
    }, 1000);
  }
};

const isTrialSession = computed(() => {
  return (
    !paymentStore.isSubscribed?.[bank.value?.name]?.val &&
    dataStore.sessionsDataOrdered.length === 0
  );
});
</script>

<style scoped>
.nice-grad-background {
  background: linear-gradient(
    to right,
    rgb(236, 72, 153),
    rgb(239, 68, 68),
    rgb(234, 179, 8)
  ) !important;
}

.cat-stats {
  display: grid;
  grid-template-columns: max-content 1fr; /* labels column + values column */
  column-gap: 8px;
  row-gap: 4px;
  align-items: baseline;
  margin-top: 6px; /* space under the progress bar */
}

.cat-stats .label::after {
  content: ":";
  margin-left: 2px;
}

@media (min-width: 600px) {
  /* On sm+ screens, show two pairs per row for tighter layout */
  .cat-stats {
    grid-template-columns: max-content 1fr max-content 1fr;
  }
}
</style>
